import { router, useForm } from '@inertiajs/react'
import { GitBranch, Plus, RefreshCw, Trash2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import WorkflowChecklist from '@/frontend/features/Praxis/WorkflowChecklist'

export interface CredentialPraxisAccess {
  configured: boolean
  available: boolean
  organization_id: string
  organization_name: string
  workflows: Array<{ name: string; title: string }>
  projects: Array<{ id: string; name: string }>
  grants: Array<{ id: string; workflow_name: string; project_id: string | null; scope: string; current: boolean }>
  copy: Record<string, string>
}

type Props = { credentialId: string; access: CredentialPraxisAccess; canManage: boolean }

export default function CredentialWorkflowAccess({ credentialId, access, canManage }: Props) {
  const form = useForm({ project_id: '', workflow_names: [] as string[] })
  const copy = access.copy
  const path = `/dashboard/credentials/${credentialId}/praxis_grants`
  const selectedScope = form.data.project_id || null
  const existing = new Set(access.grants.filter((grant) => grant.project_id === selectedScope).map((grant) => grant.workflow_name))
  const options = { ...access, workflows: access.workflows.filter((item) => !existing.has(item.name)) }

  return <section className="space-y-5 rounded-xl border border-border bg-card p-5 sm:p-6" aria-label={copy.title}>
    <header className="flex flex-wrap items-start justify-between gap-3">
      <div className="max-w-2xl">
        <h2 className="flex items-center gap-2 text-sm font-semibold"><GitBranch className="size-4 text-muted-foreground" strokeWidth={1.75} />{copy.title}</h2>
        <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">{copy.description}</p>
      </div>
      {canManage && access.configured && access.grants.length > 0 && <Button type="button" variant="outline" size="sm" onClick={() => router.post(`${path}/sync`, {}, { preserveScroll: true })}>
        <RefreshCw className="size-3.5" />{copy.sync}
      </Button>}
    </header>

    {access.grants.length > 0 ? <ul className="divide-y divide-border rounded-lg border border-border">
      {access.grants.map((grant) => <li key={grant.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <div className="min-w-0 space-y-1">
          <p className="break-all text-[13px] font-medium">{grant.workflow_name}</p>
          <p className="text-xs text-muted-foreground">{grant.scope}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={grant.current ? 'success' : 'secondary'}>{grant.current ? copy.active : copy.paused}</Badge>
          {canManage && <Button type="button" variant="ghost" size="sm" className="text-muted-foreground hover:text-destructive"
            aria-label={`${copy.remove} ${grant.workflow_name}`} onClick={() => router.delete(`${path}/${grant.id}`, { preserveScroll: true })}>
            <Trash2 className="size-3.5" />{copy.remove}
          </Button>}
        </div>
      </li>)}
    </ul> : <p className="rounded-lg border border-dashed border-border px-4 py-5 text-[13px] text-muted-foreground">{copy.none}</p>}

    {!access.configured && <p role="status" className="text-[13px] text-muted-foreground">{copy.not_configured}</p>}
    {canManage && access.configured && <form className="space-y-4 border-t border-border pt-5" onSubmit={(event) => {
      event.preventDefault()
      form.post(path, { preserveScroll: true, onSuccess: () => form.reset() })
    }}>
      <div className="space-y-2">
        <Label htmlFor="praxis-credential-scope">{copy.scope}</Label>
        <select id="praxis-credential-scope" className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm sm:max-w-md"
          value={form.data.project_id} disabled={form.processing} onChange={(event) => form.setData({ project_id: event.target.value, workflow_names: [] })}>
          <option value="">{copy.organization_scope}</option>
          {access.projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
        </select>
        <p className="text-xs text-muted-foreground">{copy.scope_hint}</p>
      </div>
      <WorkflowChecklist options={options} selected={form.data.workflow_names} copy={copy} disabled={form.processing}
        onChange={(names) => form.setData('workflow_names', names)} />
      <div className="flex justify-end"><Button type="submit" disabled={form.processing || !access.available || !form.data.workflow_names.length}>
        <Plus className="size-4" />{form.processing ? copy.saving : copy.save}
      </Button></div>
    </form>}
  </section>
}
